import { useTranslation } from '@fluxora/i18n';

import type { MeterField } from '../services/meterDetail.service';

/**
 * A shared `<dl>` renderer for meter fields (SHOP-3 / SHOP-4). Both the compact
 * `MeterSummaryCard` and the full `MeterDetailsDialog` map their rows — derived
 * in `services/meterDetail.service.ts` — through this component so value
 * rendering (a literal `text` vs an i18n `valueKey`) is decided in one place.
 *
 * A row may also carry a `badgeKey` — a warning that the value is not what its
 * heading claims (E-SF19 P2). It renders beneath the value, never instead of it.
 */
export interface MeterFieldListProps {
  readonly fields: readonly MeterField[];
  /** Column layout: the card uses two, the dialog can request one. */
  readonly columns?: 1 | 2;
}

export function MeterFieldList({ fields, columns = 2 }: MeterFieldListProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <dl className={columns === 2 ? 'grid grid-cols-1 gap-2 sm:grid-cols-2' : 'grid grid-cols-1 gap-2'}>
      {fields.map((field) => (
        <div key={field.id} className="flex flex-col gap-0.5">
          <dt className="text-[11px] font-medium uppercase tracking-wide text-foreground/50">
            {t(field.labelKey)}
          </dt>
          <dd className="text-sm text-foreground">
            {field.text !== undefined ? field.text : t(field.valueKey ?? '', field.valueParams)}
          </dd>
          {/*
            E-SF19 P2 — a value that is NOT what the heading claims says so, right
            where it is read. A synthesised example address rendered under "Meter
            details confirmed" with no badge is the case this exists for.
            `role="note"` rather than an alert: it is a qualification of the value,
            not an error the customer has to act on.
          */}
          {field.badgeKey === undefined ? null : (
            <span
              role="note"
              data-testid={`meter-field-badge-${field.id}`}
              className="text-[11px] font-medium text-warning"
            >
              {t(field.badgeKey)}
            </span>
          )}
        </div>
      ))}
    </dl>
  );
}
